import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "@/features/auth/context/AuthContext";
import { notificationHistoryService } from "@/features/notifications/services/notificationService";
import type { NotificationHistoryItem } from "@/features/notifications/types/notification";

interface NotificationContextType {
  notifications: NotificationHistoryItem[];
  unreadCount: number;
  fetchNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markMultipleAsRead: (ids: string[]) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  unreadCount: 0,
  fetchNotifications: async () => {},
  markAsRead: async () => {},
  markMultipleAsRead: async () => {},
});

export const useNotificationContext = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<NotificationHistoryItem[]>([]);

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await notificationHistoryService.getMyNotifications();
      setNotifications(data);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  }, []);

  const markAsRead = useCallback(async (id: string) => {
    try {
      await notificationHistoryService.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.notification_id === id ? { ...n, is_read: true } : n)),
      );
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  }, []);

  const markMultipleAsRead = useCallback(async (ids: string[]) => {
    try {
      await Promise.all(ids.map((id) => notificationHistoryService.markAsRead(id)));
      setNotifications((prev) =>
        prev.map((n) => (ids.includes(n.notification_id) ? { ...n, is_read: true } : n)),
      );
    } catch (error) {
      console.error("Failed to mark notifications as read:", error);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
      // Poll every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, fetchNotifications]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <NotificationContext.Provider
      value={{ notifications, unreadCount, fetchNotifications, markAsRead, markMultipleAsRead }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
